import { daysInMonth, realDay, SbDateParts, SbTimeParts } from './sb-date-time-parse';

/**
 * The panel's one date and time format. Dates are `MM/DD/YYYY` and times `h:mm A`, as in the
 * tables, the server's strings and `mediumDate`; weeks start on Sunday and names are English.
 * Change the format here and every date, time and range field follows. While a field is focused
 * its hour segment is zero-padded (`09:30 AM`); at rest it shows the stored form (`9:30 AM`).
 * Neither is ever a value.
 */
export type SbDateTimeMode = 'date' | 'time' | 'datetime';

/** What the date and time fields write: today's text contracts, or a `Date` (date mode only). */
export type SbDateValueAs = 'text' | 'date';

/**
 * The text a datetime field writes for a whole date and time: `2026-10-28T21:30`, or the 12-hour
 * `2026-10-28 09:30 PM` the speaker sessions were saved in, which the server also reads.
 */
export type SbDateTimeValueFormat = 'YYYY-MM-DDTHH:mm' | 'YYYY-MM-DD hh:mm A';

export const SB_LOCALE = 'en-US';

const DATE_FORMAT_NAME = 'mm/dd/yyyy';

const pad = (value: number): string => String(value).padStart(2, '0');

export const formatDateDisplay = (date: SbDateParts): string => `${pad(date.month + 1)}/${pad(date.day)}/${date.year}`;

/** `9:30 PM`, or `09:30 PM` while a field is focused. */
export const formatTimeDisplay = (time: SbTimeParts, padded = false): string => {
  const hour = time.hour % 12 || 12;
  return `${padded ? pad(hour) : hour}:${pad(time.minute)} ${time.hour < 12 ? 'AM' : 'PM'}`;
};

export const formatDateTimeDisplay = (date: SbDateParts, time: SbTimeParts | null, padded = false): string =>
  time ? `${formatDateDisplay(date)}, ${formatTimeDisplay(time, padded)}` : formatDateDisplay(date);

export const isoDate = (date: SbDateParts): string => `${date.year}-${pad(date.month + 1)}-${pad(date.day)}`;

/** `YYYY-MM-DD`, or a longer string that starts with one (the report's `minMax` values). */
export const parseIsoDate = (value: unknown, prefix = false): SbDateParts | null => {
  const match = typeof value === 'string' ? value.trim().match(prefix ? /^(\d{4})-(\d{2})-(\d{2})/ : /^(\d{4})-(\d{2})-(\d{2})$/) : null;
  return match ? realDay(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

export const dateParts = (date: Date): SbDateParts => ({ year: date.getFullYear(), month: date.getMonth(), day: date.getDate() });

/**
 * The day a range bound stands for: `YYYY-MM-DD`, or the local day of a longer timestamp (the
 * report's `minMax` instants), as moment read it for the page and its request.
 */
export const boundValueDay = (value: string | null): SbDateParts | null => {
  const exact = parseIsoDate(value);
  if (exact || !value) {
    return exact;
  }
  const instant = new Date(value);
  return parseIsoDate(value, true) && !isNaN(instant.getTime()) ? dateParts(instant) : parseIsoDate(value, true);
};

export const sameDay = (a: SbDateParts | null, b: SbDateParts | null): boolean =>
  !!a && !!b && a.year === b.year && a.month === b.month && a.day === b.day;

/** A number that orders days. */
export const dayNumber = (date: SbDateParts): number => date.year * 10000 + (date.month + 1) * 100 + date.day;

/** `Oct 28, 2026`, the form the field's messages name a bound in. */
export const formatLongDay = (date: SbDateParts): string =>
  new Date(date.year, date.month, date.day).toLocaleDateString(SB_LOCALE, { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * A range as its trigger names it: `Oct 1 – Oct 28, 2026`, `Dec 20, 2026 – Jan 5, 2027`, or one
 * side, `from Oct 1, 2026` / `until Oct 28, 2026`. '' with neither.
 */
export const formatRangeDays = (start: SbDateParts | null, end: SbDateParts | null): string => {
  if (start && end) {
    const from = start.year === end.year
      ? new Date(start.year, start.month, start.day).toLocaleDateString(SB_LOCALE, { month: 'short', day: 'numeric' })
      : formatLongDay(start);
    return `${from} – ${formatLongDay(end)}`;
  }
  if (start) {
    return `from ${formatLongDay(start)}`;
  }
  return end ? `until ${formatLongDay(end)}` : '';
};

/** `Wednesday, October 28, 2026`, for the trigger's name and announcements. */
export const formatSpokenDay = (date: SbDateParts): string =>
  new Date(date.year, date.month, date.day).toLocaleDateString(SB_LOCALE, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

/**
 * A `min`/`max` as a calendar day: a `Date` counts as its local day, a string is `YYYY-MM-DD` or
 * `MM/DD/YYYY` (client add's end date takes the start's text as its minimum).
 */
export const boundDay = (bound: Date | string | number | null | undefined): SbDateParts | null => {
  if (bound instanceof Date) {
    return isNaN(bound.getTime()) ? null : dateParts(bound);
  }
  if (typeof bound !== 'string' || !bound.trim()) {
    return null;
  }
  const iso = parseIsoDate(bound, true);
  if (iso) {
    return iso;
  }
  const us = bound.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return us ? realDay(Number(us[3]), Number(us[1]) - 1, Number(us[2])) : null;
};

/** Which side of `min`..`max` a day falls on; null when inside. */
export const outOfBounds = (date: SbDateParts, min: SbDateParts | null, max: SbDateParts | null): 'min' | 'max' | null => {
  if (min && dayNumber(date) < dayNumber(min)) {
    return 'min';
  }
  return max && dayNumber(date) > dayNumber(max) ? 'max' : null;
};

/** What a field holds, read in its mode (`readField` in `sb-date-time-segments.ts`). */
export interface SbFieldReading {
  empty: boolean;
  date: SbDateParts | null;
  time: SbTimeParts | null;
  /** A whole value for the mode: a datetime may be a date alone. */
  complete: boolean;
}

/** The time text the time fields write: `h:mm A`, unpadded ("3:30 PM"), as `formatTimeText` does. */
export const timeValueText = (time: SbTimeParts): string => formatTimeDisplay(time, false);

const hhmm = (time: SbTimeParts): string => `${pad(time.hour)}:${pad(time.minute)}`;

const hhmmA = (time: SbTimeParts): string => `${pad(time.hour % 12 || 12)}:${pad(time.minute)} ${time.hour < 12 ? 'AM' : 'PM'}`;

/**
 * The text contract (`valueAs="text"`, unchanged from the fields these replace):
 * - date: `YYYY-MM-DD` once the day is whole and real, `''` before;
 * - time: `h:mm A` once it reads, else the text the field shows (`9:3m PM`), so validation flags it;
 * - datetime: `YYYY-MM-DDTHH:mm` (or `YYYY-MM-DD hh:mm A`, `format`), a date alone as
 *   `YYYY-MM-DD`, a time alone as `h:mm A`; `''` while a part does not read, as the native
 *   input did, so a page that reads the value without its validity never gets half of it.
 */
export const textValue = (mode: SbDateTimeMode, reading: SbFieldReading, typed: string, format: SbDateTimeValueFormat = 'YYYY-MM-DDTHH:mm'): string => {
  if (reading.empty) {
    return '';
  }
  if (mode === 'date') {
    return reading.date ? isoDate(reading.date) : '';
  }
  if (mode === 'time') {
    return reading.time ? timeValueText(reading.time) : typed.trim();
  }
  if (!reading.complete) {
    return '';
  }
  if (reading.date && reading.time) {
    return format === 'YYYY-MM-DD hh:mm A' ? `${isoDate(reading.date)} ${hhmmA(reading.time)}` : `${isoDate(reading.date)}T${hhmm(reading.time)}`;
  }
  if (reading.date) {
    return isoDate(reading.date);
  }
  return reading.time ? timeValueText(reading.time) : '';
};

/** Whether a text ends with a zone (`Z`, `+02:00`): an instant the field, which holds wall times, cannot show without converting. */
export const hasZone = (text: string): boolean => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text.trim());

/** The hidden hint every field links through `aria-describedby`; a disabled or readonly field has no panel to open. */
export const formatHint = (mode: SbDateTimeMode | 'range', interactive = true): string => {
  const keys = interactive ? ' Type the digits or paste a date; Up and Down change the focused part.' : '';
  if (mode === 'time') {
    return `Time, hour and minutes, then A or P.${interactive ? ' Type the digits or paste a time; Up and Down change the focused part. Alt+Down opens the time picker.' : ''}`;
  }
  if (mode === 'datetime') {
    return `Date and time, month/day/year then hour, minutes and A or P.${interactive ? `${keys} Alt+Down opens the calendar.` : ''}`;
  }
  return `Date, month/day/year.${interactive ? `${keys} Alt+Down opens the calendar.` : ''}`;
};

/** The time format as the messages name it, with either half of the day. */
const TIME_FORMAT_NAME = 'hh:mm am/pm';

/** "Invalid date: 10/2 (use mm/dd/yyyy)", in the sb-input phrasing. */
export const invalidTextMessage = (mode: SbDateTimeMode, text: string): string => {
  if (mode === 'time') {
    return `Invalid time: ${text} (use ${TIME_FORMAT_NAME})`;
  }
  return mode === 'datetime'
    ? `Invalid date and time: ${text} (use ${DATE_FORMAT_NAME}, ${TIME_FORMAT_NAME})`
    : `Invalid date: ${text} (use ${DATE_FORMAT_NAME})`;
};

export const boundMessage = (side: 'min' | 'max', bound: SbDateParts): string =>
  side === 'min' ? `Date must be on or after ${formatLongDay(bound)}` : `Date must be on or before ${formatLongDay(bound)}`;

/** The display-only notice under a text-mode field whose typed date is outside `min`..`max`. */
export const boundNotice = (side: 'min' | 'max', bound: SbDateParts): string =>
  side === 'min'
    ? `Date is before ${formatLongDay(bound)}, the earliest allowed`
    : `Date is after ${formatLongDay(bound)}, the latest allowed`;

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Why a month, or a day in it, cannot be typed: "There is no month 13", "April has 30 days",
 * "February has 28 days in 2027" (with no year yet, "February has at most 29 days"). '' when it can.
 */
export const impossibleDateMessage = (month: number, day: number | null, year: number | null): string => {
  if (month < 1 || month > 12) {
    return `There is no month ${String(month).padStart(2, '0')}`;
  }
  if (day === null) {
    return '';
  }
  if (day < 1) {
    return 'There is no day 00';
  }
  const days = daysInMonth(year ?? 2000, month - 1);
  if (day <= days) {
    return '';
  }
  if (year !== null) {
    return `${MONTH_NAMES[month - 1]} has ${days} days in ${year}`;
  }
  return month === 2 ? 'February has at most 29 days' : `${MONTH_NAMES[month - 1]} has ${days} days`;
};
